// LOAD-BEARING SIDE-EFFECT IMPORT — do not delete as "unused".
// The hub import warmed the Rematch model cluster; this specifier restores
// that init order. It retires with the Z2 codemod.
import '@/pages/api/scribeAPIV2';

import { documentPrintedV2 } from '@/lib/analytics/scribe/documents';
import { extractAxiosErrorDetail } from '@heidi/api/errors';
import { toast } from '@heidi/design-system/toast';
import {
  letterSenderIsDownloadingPdfAtomFamily,
  letterSenderIsSendingAtomFamily,
  letterSenderMessages,
  useLetter,
  useLetterSenderStore,
  usePageEditor,
} from '@heidi/integrations/letters';
import { useMutateSendLetter } from '@heidi/integrations/letters/data';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { LetterDeliveryStatus } from '@heidi/schema/ml-scribe';
import { documentExportPdf, useReviewConfirmationGate } from '@heidi/session/data';
import { useExportFileName } from '@heidi/utils';
import { isAxiosError } from 'axios';
import { useAtom } from 'jotai';
import { useCallback, type MouseEvent } from 'react';
import { useIntl } from 'react-intl';
import { usePatientProfile } from '../components/letter-tab-pane/usePatientProfile';

type HandleSendLetterOptions = {
  onBeforeSend?: () => void;
  onError?: () => void;
  onSuccess?: () => void;
};

const isMouseEvent = (
  value: HandleSendLetterOptions | MouseEvent | undefined
): value is MouseEvent => Boolean(value && 'nativeEvent' in value);

export const useLetterSenderActions = () => {
  const intl = useIntl();
  const { doActionWithConfirmation } = useReviewConfirmationGate();
  const { getExportFileName } = useExportFileName();
  const sessionId = useCurrentSessionId();
  const letter = useLetter((data) => data);
  const letterId = letter?.id ?? '';
  const { patientProfile } = usePatientProfile();
  const pageEditor = usePageEditor();
  const [isSendingRequested, setIsSendingRequested] = useAtom(
    letterSenderIsSendingAtomFamily(letterId)
  );
  const setIsEditingMessageAfterFailedSend = useLetterSenderStore(
    (s) => s.setIsEditingMessageAfterFailedSend
  );
  const [isDownloadingLetterPdf, setIsDownloadingLetterPdf] = useAtom(
    letterSenderIsDownloadingPdfAtomFamily(letterId)
  );
  const { mutateAsync: mutateSendLetter, isPending: isSendMutationPending } =
    useMutateSendLetter({ sessionId: sessionId ?? '' });
  const isSendingLetter = isSendingRequested || isSendMutationPending;

  const handleSendLetter = useCallback(
    (options?: HandleSendLetterOptions | MouseEvent) => {
      const { onBeforeSend, onError, onSuccess } = isMouseEvent(options)
        ? {}
        : (options ?? {});
      if (isSendingLetter) {
        return;
      }

      if (!sessionId || !letterId || !pageEditor) {
        toast({
          tone: 'destructive',
          title: intl.formatMessage(letterSenderMessages.noDocumentId),
        });
        return;
      }

      setIsSendingRequested(true);
      setIsEditingMessageAfterFailedSend(false);
      onBeforeSend?.();

      void (async () => {
        try {
          const pdfFile = await pageEditor.getPdf();

          if (!pdfFile) {
            throw new Error('Page editor PDF generation failed');
          }

          // The send endpoint returns 200 even when delivery fails inline
          // (e.g. SES rejection), with the per-recipient outcome in the body —
          // only celebrate an actual delivery.
          const sendResponse = await mutateSendLetter({ letterId, pdfFile });

          if (sendResponse?.delivery_status === LetterDeliveryStatus.FAILED) {
            onError?.();

            toast({
              tone: 'destructive',
              title: intl.formatMessage(letterSenderMessages.letterSentError),
            });
          } else {
            onSuccess?.();

            toast({
              tone: 'success',
              title: intl.formatMessage(letterSenderMessages.letterSentSuccess),
            });
          }
        } catch (error) {
          onError?.();
          // A 422 from the send endpoint is a data problem the user can fix
          // (e.g. a missing DOB for HealthLink) — surface the backend's detail
          // instead of the generic retry copy. 5xx and everything else keep it.
          const detail =
            isAxiosError(error) && error.response?.status === 422
              ? extractAxiosErrorDetail(error)
              : undefined;

          toast({
            tone: 'destructive',
            title: detail ?? intl.formatMessage(letterSenderMessages.letterSentError),
          });
        } finally {
          setIsSendingRequested(false);
        }
      })();
    },
    [
      intl,
      isSendingLetter,
      letterId,
      mutateSendLetter,
      pageEditor,
      sessionId,
      setIsEditingMessageAfterFailedSend,
      setIsSendingRequested,
    ]
  );

  const handlePrintLetterPdf = useCallback(() => {
    doActionWithConfirmation(() => {
      void (async () => {
        const didPrint = await pageEditor.printPdf();
        if (!didPrint) return;

        documentPrintedV2({
          sessionId,
          documentId: letterId,
          export_context: 'letter_sender',
          is_pinned_template: false,
          is_starter_pack_template: false,
        });
      })();
    }, 'export');
  }, [doActionWithConfirmation, letterId, pageEditor, sessionId]);

  const handleDownloadLetterPdf = useCallback(() => {
    if (isDownloadingLetterPdf) {
      return;
    }

    doActionWithConfirmation(() => {
      void (async () => {
        setIsDownloadingLetterPdf(true);

        try {
          const file = await pageEditor.getPdf();
          if (!file) return;

          const fileName = getExportFileName({
            documentName: 'Letter',
            patientDemographicString: patientProfile?.demographic_string,
          });

          const url = URL.createObjectURL(file);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${fileName}.pdf`;
          link.click();
          URL.revokeObjectURL(url);

          documentExportPdf({
            sessionId: sessionId ?? '',
            documentId: letterId,
            export_context: 'letter_sender',
          });
        } finally {
          setIsDownloadingLetterPdf(false);
        }
      })();
    }, 'export');
  }, [
    doActionWithConfirmation,
    getExportFileName,
    isDownloadingLetterPdf,
    letterId,
    pageEditor,
    patientProfile?.demographic_string,
    sessionId,
    setIsDownloadingLetterPdf,
  ]);

  return {
    handleDownloadLetterPdf,
    handlePrintLetterPdf,
    handleSendLetter,
    isDownloadingLetterPdf,
    isSendingLetter,
  };
};
